import {type QueryClient, useQueryClient} from '@github-ui/react-query'
import {useMemo, useState} from 'react'

import {
  type TimelineIssue,
  type TimelineItemsConnection,
  type TimelineItemsPageParams,
  timelineItemsQueryKey,
} from './fetch-timeline-items'

/**
 * # Opening the timeline without asking the server again
 *
 * The viewer opens on two windows -- the oldest comments and the newest, with a gap between them.
 * The page query already returns both, so neither needs fetching. Putting them in the cache *before*
 * the timeline renders is what makes that true: the windows are ordinary queries, and a query whose
 * key is already warm neither fetches nor suspends.
 *
 * Without this every issue page load spent two round trips -- one after the other, since the second
 * needs a cursor from the first -- re-fetching rows the page had already delivered, and the newest
 * comments could not appear in the server-rendered HTML at all, because nothing had them until the
 * browser asked.
 *
 * The oldest window is stored as it arrives. The newest is rebuilt first, because the key it lives
 * under is not the one the page query asked for. That is the anchor, below.
 *
 * ## The anchor
 *
 * `last: N` counts back from the end of the timeline, so the window it selects **slides**: every new
 * comment pushes an older one out, and a reader watching the newest comments sees the top one
 * vanish. So the window is pinned to a cursor instead -- `{last: 150, after: <cursor>}` -- naming the
 * row immediately *before* the first one it shows. Pinned, it **grows** as comments arrive.
 *
 * `after: X` returns the rows after X, so that cursor has to name a row the window itself does not
 * contain. Which is why the page query asks for one row more than it renders:
 *
 * ```text
 * payload   last: 16                =>  26 27 28 ... 41
 *                                       ^^ the anchor
 * stored    last: 150, after: <26>  =>     27 28 ... 41
 * ```
 *
 * Dropping row 26 leaves exactly what the pinned read would have returned, which is what makes this
 * a real substitute for that request rather than an approximation. Nor is row 26 lost: on a long
 * timeline it falls in the gap the "N remaining" marker counts, and on a short one the oldest window
 * is already showing it.
 *
 * ## Who owns each window
 *
 * The two windows are not symmetric, and the rules in `installWindows` follow from that.
 *
 * The **oldest** window stays *server-owned* for the whole visit. Its rows are the first fifteen and
 * do not change as the timeline grows, so every payload still describes it and may replace it. That
 * is also the only route by which an optimistic edit -- a pinned badge, a reaction -- reaches a
 * timeline that renders from the cache rather than from the payload.
 *
 * The **newest** window is server-owned only until it is first stored, then *reader-owned*. From
 * there it is theirs to grow: comments they post settle into it, and a pinned refetch returns
 * everything after the anchor. A later payload can no longer describe it, because by then its own
 * newest 16 rows begin further down and name a different anchor.
 *
 * The consequence worth knowing: nothing outside the timeline can repair the newest window. A route
 * payload refresh no longer replaces it, and while it is on screen nothing refetches it on its own
 * -- focus refetching is off globally. So a live update that never arrives leaves it stale until the
 * next one does. `use-timeline-sections.ts` turns reconnect refetching back on for exactly that
 * reason: reconnecting is the one moment we know a notification may have been missed.
 */

/** The endpoint's cap on a single page, and what the pinned window asks for every time it reads. */
const MAX_SERVER_PAGE_SIZE = 150

/** Rows in each opening window. Matches the Relay path, so both produce the same initial view. */
export const DEFAULT_TIMELINE_PAGE_SIZE = 15

/**
 * Stores the payload's two windows for the timeline to find, and returns the cursor the newest one
 * is pinned to. Callers hand that cursor to the timeline, which builds the same key and finds its
 * rows already there.
 *
 * Runs during render, ahead of the timeline mounting, because a window stored afterwards arrives
 * once the section has already decided to fetch. Safe because query-core notifies observers
 * asynchronously, so a write here cannot update a mounted component mid-render.
 */
export function useTimelinePrefill({
  coordinates,
  pageSize = DEFAULT_TIMELINE_PAGE_SIZE,
  issue,
  backIssue,
  payloadUpdatedAt,
  enabled = true,
}: {
  /**
   * The issue whose windows these are. Absent together rather than individually, and only on a
   * render with no issue to show -- the route cannot check for that before calling this, because
   * the check throws and a hook may not follow a throw. Nothing is stored on such a render.
   */
  coordinates: {owner: string; repo: string; number: number} | undefined
  pageSize?: number
  /** The payload's oldest window. */
  issue: TimelineIssue | undefined
  /** The payload's newest window, one row longer than it renders. Its first row is the anchor. */
  backIssue: TimelineIssue | undefined
  /** When the payload was read. Anything the cache has learned since outranks it. */
  payloadUpdatedAt: number
  /** Off for the crawler's isolated window, which reads one explicit page and stores nothing. */
  enabled?: boolean
}): string | undefined {
  const queryClient = useQueryClient()
  const issueKey = coordinates && `${coordinates.owner}/${coordinates.repo}/${coordinates.number}`

  /**
   * The anchor is fixed for as long as the reader stays on the issue, which is the point of pinning:
   * hold the cursor still and the window grows; recompute it and the window slides, which is the
   * thing pinning exists to prevent.
   *
   * Every later payload names a *different* anchor, because by then the timeline is longer and its
   * newest 16 rows begin further down. So the first anchor an issue produces is kept, and the window
   * it pins is left alone from then on.
   */
  const [pinned, setPinned] = useState<{issueKey: string; cursor: string} | undefined>(undefined)
  const pinnedCursor = pinned && issueKey && pinned.issueKey === issueKey ? pinned.cursor : undefined

  const cursor = useMemo(() => {
    if (!enabled || !coordinates) return undefined

    const prefill = buildTimelinePrefill({issue, backIssue, pageSize})
    if (!prefill) return undefined

    installWindows(queryClient, {
      ...coordinates,
      prefill,
      payloadUpdatedAt,
      newestWindowIsPinned: pinnedCursor !== undefined,
    })

    return prefill.anchorCursor
  }, [enabled, coordinates, pageSize, issue, backIssue, payloadUpdatedAt, pinnedCursor, queryClient])

  if (cursor && pinnedCursor === undefined && issueKey) setPinned({issueKey, cursor})

  return pinnedCursor ?? cursor
}

/** The key the oldest window is stored under, and the one the timeline reads it back from. */
export const frontParamsFor = (pageSize: number): TimelineItemsPageParams => ({first: pageSize})

/** The key the newest window takes once it has an anchor to pin to. */
export const anchoredBackParamsFor = (anchorCursor: string): TimelineItemsPageParams => ({
  last: MAX_SERVER_PAGE_SIZE,
  after: anchorCursor,
})

/**
 * The key the newest window takes when there is no anchor: a host that renders the viewer without a
 * payload to derive one from, or a timeline with no rows to pin to. One row longer than the window
 * renders, because that extra row is where the cursor comes from.
 */
export const unanchoredBackParamsFor = (pageSize: number): TimelineItemsPageParams => ({last: pageSize + 1})

type TimelineWindow = {
  params: TimelineItemsPageParams
  issue: TimelineIssue
  /** The pinned window, as opposed to the oldest one or an unanchored empty timeline. */
  isNewestWindow?: boolean
}

type TimelinePrefill = {
  /** The cursor the newest window is pinned to. Absent when the payload cannot supply one. */
  anchorCursor?: string
  windows: TimelineWindow[]
}

const edgesOf = (connection: TimelineItemsConnection | undefined) => connection?.edges ?? []

/**
 * Whether a window is worth storing at all. One that claims rows it did not return, or that offers
 * no cursor to page onward from, would render as though it were the whole timeline and never correct
 * itself -- so leave it to be fetched instead.
 */
function isUsableWindow(connection: TimelineItemsConnection | undefined): boolean {
  if (!connection) return false
  if ((connection.totalCount ?? 0) === 0) return true
  if (!connection.pageInfo?.startCursor && !connection.pageInfo?.endCursor) return false

  return edgesOf(connection).length > 0
}

/**
 * The cursor to pin the newest window to: its first row, provided that row really does sit outside
 * the window it pins.
 *
 * True when the payload sent the extra row. Also true on a timeline short enough that the oldest
 * window is already showing that row, where dropping it hides nothing -- which is what keeps this
 * working against a payload that predates the extra row.
 */
function findAnchorCursor({
  newest,
  oldest,
  pageSize,
}: {
  newest: TimelineItemsConnection
  oldest: TimelineItemsConnection | undefined
  pageSize: number
}): string | undefined {
  const rows = edgesOf(newest)
  const anchorRow = rows[0]
  if (!anchorRow?.cursor) return undefined

  if (rows.length === pageSize + 1) return anchorRow.cursor

  const anchorId = anchorRow.node?.id
  const alreadyShown = Boolean(anchorId) && edgesOf(oldest).some(edge => edge?.node?.id === anchorId)

  return alreadyShown ? anchorRow.cursor : undefined
}

/**
 * Rebuilds the payload's newest window as the pinned read of the same moment: drop the anchor row,
 * keep everything else. Every row, and every field on it, is still the server's.
 *
 * Only the page info has to be restated, because the two reads genuinely disagree about it.
 * `hasPreviousPage` is false here: the payload's read sliced a longer timeline with `last` and so
 * reports true, while a read whose interval was chosen by a cursor reports false however many rows
 * precede it.
 */
export function asPinnedTimelineWindow(connection: TimelineItemsConnection): TimelineItemsConnection {
  const rows = edgesOf(connection).slice(1)

  return {
    ...connection,
    edges: rows,
    pageInfo: {
      ...connection.pageInfo,
      startCursor: rows[0]?.cursor ?? null,
      hasPreviousPage: false,
      hasNextPage: false,
    },
  }
}

/**
 * Turns the page payload into the windows to store, and the anchor to pin the newest one to.
 *
 * Gives up entirely when the oldest window is unusable, rather than storing half a timeline: the
 * newest window cannot render an issue on its own, and a window left to be fetched always beats one
 * stored with the wrong rows.
 */
export function buildTimelinePrefill({
  issue,
  backIssue,
  pageSize,
}: {
  issue: TimelineIssue | undefined
  backIssue: TimelineIssue | undefined
  pageSize: number
}): TimelinePrefill | undefined {
  const oldest = issue?.timelineItems
  if (!issue || !isUsableWindow(oldest)) return undefined

  const windows: TimelineWindow[] = [{params: frontParamsFor(pageSize), issue}]

  const newest = backIssue?.timelineItems
  if (!backIssue || !newest) return {windows}

  // An empty timeline has no row to pin to and none to hide, so store it under the key the window
  // reads before it has an anchor. A new issue then opens without a request either.
  if ((newest.totalCount ?? 0) === 0) {
    return {windows: [...windows, {params: unanchoredBackParamsFor(pageSize), issue: backIssue}]}
  }

  const anchorCursor = findAnchorCursor({newest, oldest, pageSize})
  if (!anchorCursor) return {windows}

  return {
    anchorCursor,
    windows: [
      ...windows,
      {
        params: anchoredBackParamsFor(anchorCursor),
        issue: {...backIssue, timelineItems: asPinnedTimelineWindow(newest)},
        isNewestWindow: true,
      },
    ],
  }
}

/**
 * Writes the windows into the cache, dated as of the payload rather than as of now.
 *
 * That date is what lets one rule serve two opposing needs. An optimistic edit rebuilds the payload
 * without a new read behind it, so its write carries the same date as the last and is stored --
 * which is how a pinned badge or a reaction reaches a timeline rendering from the cache. Anything the
 * cache learned on its own, though -- a comment the reader posted, a window a live update refetched
 * -- is dated later than the payload and is left alone. A genuinely newer payload outranks both.
 *
 * Cancel before writing: `setQueryData` does not stop a read already in flight, and one landing
 * afterwards would put its older rows back.
 */
function installWindows(
  queryClient: QueryClient,
  {
    owner,
    repo,
    number,
    prefill,
    payloadUpdatedAt,
    newestWindowIsPinned,
  }: {
    owner: string
    repo: string
    number: number
    prefill: TimelinePrefill
    payloadUpdatedAt: number
    /** Set once the reader has the newest window, so a later payload cannot re-pin it. */
    newestWindowIsPinned?: boolean
  },
): void {
  for (const {params, issue, isNewestWindow} of prefill.windows) {
    if (isNewestWindow && newestWindowIsPinned) continue

    const queryKey = timelineItemsQueryKey(owner, repo, number, params)
    const cached = queryClient.getQueryState(queryKey)
    if (cached?.data !== undefined && cached.dataUpdatedAt > payloadUpdatedAt) continue

    void queryClient.cancelQueries({queryKey, exact: true}, {silent: true})
    queryClient.setQueryData(queryKey, issue, {updatedAt: payloadUpdatedAt})
  }
}
